import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";

import { ApiError, chunkedUpload } from "@/lib/api";
import { queryClient } from "@/lib/queryClient";

const UploadCtx = createContext(null);
const CONCURRENCY = 2;
let seq = 0;

/** App-wide upload queue: survives navigation, per-file progress, no duplicates (each key starts once per attempt). */
export function UploadProvider({ children }) {
  const [items, setItems] = useState([]);
  const running = useRef(new Set());

  const patch = (key, p) => setItems((list) => list.map((i) => (i.key === key ? { ...i, ...p } : i)));

  const start = useCallback((item) => {
    running.current.add(item.key);
    patch(item.key, { status: "uploading", progress: 0, error: undefined });
    chunkedUpload(
      item.projectId,
      item.file,
      (p) => patch(item.key, { progress: p }),
      (uploadId) => patch(item.key, { uploadId }),
      item.uploadId,
    )
      .then(() => {
        setItems((list) => list.filter((i) => i.key !== item.key));
        queryClient.invalidateQueries({ queryKey: ["project", item.projectId] });
        queryClient.invalidateQueries({ queryKey: ["projects"] });
      })
      .catch((e) => {
        const status = e instanceof ApiError ? e.status : 0;
        if (status === 413 || status === 415) patch(item.key, { status: "rejected", error: e.message, uploadId: undefined });
        else patch(item.key, { status: "failed", error: "Upload failed" });
      })
      .finally(() => {
        running.current.delete(item.key);
        setItems((list) => [...list]); // wake the queue
      });
  }, []);

  useEffect(() => {
    const free = CONCURRENCY - running.current.size;
    if (free <= 0) return;
    items.filter((i) => i.status === "queued" && !running.current.has(i.key)).slice(0, free).forEach(start);
  }, [items, start]);

  const busy = items.some((i) => i.status === "queued" || i.status === "uploading");
  useEffect(() => {
    if (!busy) return;
    const warn = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [busy]);

  const add = useCallback((projectId, files) => {
    setItems((list) => [
      ...list,
      ...files.map((file) => ({ key: `u${Date.now()}_${seq++}`, projectId, file, progress: 0, status: "queued" })),
    ]);
  }, []);
  const retry = useCallback((key) => patch(key, { status: "queued", progress: 0, error: undefined }), []);
  const dismiss = useCallback((key) => setItems((list) => list.filter((i) => i.key !== key)), []);

  return <UploadCtx.Provider value={{ items, add, retry, dismiss }}>{children}</UploadCtx.Provider>;
}

export function useUploads(projectId) {
  const v = useContext(UploadCtx);
  if (!v) throw new Error("useUploads outside UploadProvider");
  return { ...v, items: projectId ? v.items.filter((i) => i.projectId === projectId) : v.items };
}
